/**
 * AR Vision Demo — in-memory fixture store.
 *
 * THROWAWAY DEMO CODE. A tiny external store (no extra deps — built on
 * `useSyncExternalStore`) holding the whole demo dataset. It lives at module
 * scope so the AR hook shims (which are plain functions, not components nested
 * under a provider) can read the current snapshot synchronously, while React
 * components subscribe for reactive updates.
 *
 * Usage:
 *   - `getArDemoState()`            — synchronous read (hook shims, scripts).
 *   - `useArDemoStore(selector)`    — reactive read in a component.
 *   - `updateArDemoState(producer)` — mutate (a beat applying a change).
 *   - `resetArDemoState()`          — one-click reset back to the seed.
 *
 * `<ArDemoStoreProvider>` is optional — the store works without it — but it is
 * a clear deletion boundary.
 *
 * PERSISTENCE: the snapshot is mirrored into `sessionStorage` on every mutation
 * and rehydrated at module load, so a page RELOAD mid-demo restores the live
 * state (e.g. a thread opened by a checkpoint) instead of snapping back to cold
 * open. It is per-tab (sessionStorage), so a brand-new tab still starts clean,
 * and the Reset button (`resetArDemoState`) re-seeds + overwrites the mirror.
 */

import { useRef, useSyncExternalStore } from 'react';
import type { PropsWithChildren } from 'react';
import { buildArDemoSeed } from './fixture-seed';
import type { ArDemoFixtureState } from './fixture-store.types';

// Bump the version suffix if the seed SHAPE changes, to discard stale mirrors.
// (As a backstop, `loadPersistedState` also discards a mirror whose top-level
// slices no longer match the seed — so a forgotten bump can't crash a selector
// reading a newly-added slice.)
// v2: added customAging / customerComms / salesRepEmailDraft (Reports beat).
const STORAGE_KEY = 'ar-demo-fixture-state-v3';

/** True when both objects have exactly the same set of top-level keys. */
function sameTopLevelShape(a: object, b: object): boolean {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  return aKeys.length === bKeys.length && bKeys.every((k) => k in a);
}

/**
 * Rehydrate the mirror, but DISCARD it when its top-level slices no longer match
 * the current seed (a slice was added/removed since it was written). Without this
 * a selector that reads the new slice would crash on `undefined` after a
 * seed-shape change — the `seed` arg is the fresh shape to validate against.
 */
function loadPersistedState(
  seed: ArDemoFixtureState,
): ArDemoFixtureState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ArDemoFixtureState;
    return sameTopLevelShape(parsed, seed) ? parsed : null;
  } catch {
    return null;
  }
}

function persistState(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // sessionStorage unavailable / over quota — degrade to in-memory only.
  }
}

const seedState = buildArDemoSeed();
let state: ArDemoFixtureState = loadPersistedState(seedState) ?? seedState;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Synchronous read of the current demo snapshot. */
export function getArDemoState(): ArDemoFixtureState {
  return state;
}

/**
 * Apply a mutation. The producer receives a fresh deep clone it may mutate in
 * place and/or return; whatever it returns (or the mutated clone) becomes the
 * new snapshot. Always produces a new top-level reference so subscribers
 * re-render.
 */
export function updateArDemoState(
  producer: (draft: ArDemoFixtureState) => ArDemoFixtureState | void,
): void {
  const draft = structuredClone(state);
  const next = producer(draft);
  state = next ?? draft;
  persistState();
  emit();
}

/** One-click reset: re-seed the store to its initial snapshot. */
export function resetArDemoState(): void {
  state = buildArDemoSeed();
  persistState();
  emit();
}

/**
 * Structural equality for demo snapshots. Fixture slices are plain JSON-like
 * data (objects, arrays, primitives), so a recursive compare is enough.
 */
function snapshotEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (
    typeof a !== 'object' ||
    a === null ||
    typeof b !== 'object' ||
    b === null
  ) {
    return false;
  }
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
      return false;
    }
    return a.every((item, i) => snapshotEqual(item, b[i]));
  }
  const aRec = a as Record<string, unknown>;
  const bRec = b as Record<string, unknown>;
  const aKeys = Object.keys(aRec);
  if (aKeys.length !== Object.keys(bRec).length) return false;
  return aKeys.every(
    (k) =>
      Object.prototype.hasOwnProperty.call(bRec, k) &&
      snapshotEqual(aRec[k], bRec[k]),
  );
}

/**
 * Reactive selector hook. Re-renders the caller when the selected slice changes
 * (by structural equality).
 *
 * Selectors that build a fresh object/array each call (e.g. `s => ({ customers:
 * s.inboxCustomers })`) would otherwise make `useSyncExternalStore` see a new
 * snapshot every render — an infinite update loop. We recompute the selector
 * each render (so selectors closing over args like an `id` stay correct) but
 * return the previously-cached reference whenever the result is structurally
 * unchanged, keeping the snapshot stable.
 */
export function useArDemoStore<T>(selector: (s: ArDemoFixtureState) => T): T {
  const cache = useRef<{ value: T } | null>(null);
  const getSnapshot = (): T => {
    const next = selector(state);
    if (cache.current && snapshotEqual(cache.current.value, next)) {
      return cache.current.value;
    }
    cache.current = { value: next };
    return next;
  };
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Optional provider — a structural boundary mounted only inside the demo
 * wrapper (so it never affects production) and a clear deletion marker. It no
 * longer re-seeds on mount: the store hydrates from the per-tab `sessionStorage`
 * mirror at module load, so a reload mid-demo restores the live state. A fresh
 * tab has no mirror and starts clean; the Reset button re-seeds explicitly.
 */
export function ArDemoStoreProvider({ children }: PropsWithChildren) {
  return children;
}
